import { z } from 'zod';
/**
 * A Canadian mobile number, read the same way by the server, the desk's web forms and
 * the phone.
 *
 * Canada only at launch, so the country code is fixed at +1 and a number is ten digits
 * after it (area code, exchange, line). People type numbers every way there is —
 * "(416) 555-0134", "416.555.0134", "+1 416 555 0134", "14165550134" — so the reader
 * keeps the digits, drops a leading 1 when there are eleven, and says plainly what is
 * wrong when the rest is not a number that can exist.
 *
 * What is sent and stored is always the one form, `+14165550134`. The professional's
 * number had been free text with a seven-character minimum (client, 2026-10-01: "add
 * a proper digit limit … show a clear error message if the entered number is invalid").
 */
export const PHONE_COUNTRY_CODE = '+1';
export const PHONE_NATIONAL_DIGITS = 10;
/**
 * North American numbering: neither the area code nor the exchange may start with 0
 * or 1. "416 055 0134" has ten digits and cannot be dialled.
 */
export const CANADIAN_PHONE_E164 = /^\+1[2-9]\d{2}[2-9]\d{6}$/;
/** The national digits of whatever was typed or pasted — at most ten, the +1 taken off. */
export function nationalPhoneDigits(text) {
    const digits = text.replace(/\D/g, '');
    return digits.length > PHONE_NATIONAL_DIGITS && digits.startsWith('1') ? digits.slice(1) : digits;
}
export function readCanadianPhone(text) {
    const trimmed = text.trim();
    const digits = trimmed.replace(/\D/g, '');
    if (digits.length === 0)
        return { ok: false, problem: 'empty', national: '' };
    // "+44 …" is a number, but not one this platform can text at launch.
    if (trimmed.startsWith('+') && !digits.startsWith('1')) {
        return { ok: false, problem: 'other_country', national: digits };
    }
    const national = nationalPhoneDigits(trimmed);
    if (national.length < PHONE_NATIONAL_DIGITS)
        return { ok: false, problem: 'too_short', national };
    if (national.length > PHONE_NATIONAL_DIGITS)
        return { ok: false, problem: 'too_long', national };
    const e164 = `${PHONE_COUNTRY_CODE}${national}`;
    if (!CANADIAN_PHONE_E164.test(e164))
        return { ok: false, problem: 'not_a_number', national };
    return { ok: true, e164, national };
}
/** What each problem says to the person who typed it. */
export function phoneProblemSentence(problem, national = '') {
    switch (problem) {
        case 'empty':
            return 'Enter your mobile number.';
        case 'too_short':
            return `A Canadian mobile number has 10 digits. You have entered ${national.length}.`;
        case 'too_long':
            return 'A Canadian mobile number has 10 digits. Check for an extra digit.';
        case 'other_country':
            return 'Only Canadian numbers (+1) can be used for now.';
        case 'not_a_number':
            return 'That is not a number that can be dialled. Check the area code and the next three digits.';
    }
}
/** "4165550134" as it is said: "416 555 0134". */
export function spacedPhone(national) {
    return [national.slice(0, 3), national.slice(3, 6), national.slice(6)].filter(Boolean).join(' ');
}
/** The stored form, and only that: what a client must send once it has read the number. */
export const CanadianPhone = z
    .string()
    .regex(CANADIAN_PHONE_E164, 'A Canadian mobile number, sent as +1 and ten digits, e.g. +14165550134');
//# sourceMappingURL=phone.js.map